// Phase 0085-B — Assessment Detail page.
// Single page covers:
//   - Self appraisal (employee fills SELF questions)
//   - Reporting authority appraisal (supervisor fills REPORTING questions)
//   - Reviewing authority review (reviewer fills REVIEWING questions)
//   - Employee representation (employee disagrees)
//   - HR lock (HR locks + responds to representation)
//   - Score report (visible to all)
//
// The page renders different stage actions based on the assessment's
// current_stage and the current user's relationship to the assessment.

import { useState } from 'react'
import {
  App, Alert, Badge, Button, Card, Col, Collapse, Descriptions, Empty,
  Form, Input, InputNumber, Modal, Row, Space, Tag, Typography,
} from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'

import { assessmentApi, type AssessmentQuestion, type AssessmentSection } from '@/features/hr/assessmentApi'
import { useAuthStore } from '@/features/auth/store'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Title, Paragraph } = Typography

const STAGE_COLOR: Record<string, string> = {
  DRAFT: 'default', SELF_IN_PROGRESS: 'blue', SELF_SUBMITTED: 'cyan',
  REPORTING_IN_PROGRESS: 'blue', REPORTING_SUBMITTED: 'cyan',
  REVIEWING_IN_PROGRESS: 'blue', REVIEWING_SUBMITTED: 'cyan',
  REPRESENTATION: 'orange', HR_REVIEW: 'volcano',
  LOCKED: 'green', REOPENED: 'gold',
}

const DIM_LABEL: Record<string, string> = {
  TASK_COMPLETION: 'Task Completion',
  ATTENDANCE_DISCIPLINE: 'Attendance & Discipline',
  AUTO_KPI: 'Auto KPI',
  SUPERVISOR_ASSESSMENT: 'Supervisor Assessment',
  TRAINING_SKILL: 'Training & Skill',
  OVERALL: 'Overall',
}

export default function AssessmentDetailPage() {
  const { eaId } = useParams<{ eaId: string }>()
  const navigate = useNavigate()
  const { message, modal } = App.useApp()
  const currentUserId = useAuthStore((s) => s.user?.id)

  const { data: bundle, isLoading, refetch } = useQuery({
    queryKey: ['assessment-bundle', eaId],
    queryFn: () => assessmentApi.get(eaId!),
    enabled: !!eaId,
  })

  const [reprOpen, setReprOpen] = useState(false)
  const [hrRespOpen, setHrRespOpen] = useState(false)
  const [reprForm] = Form.useForm()
  const [hrForm] = Form.useForm()

  const submitSelfMut = useMutation({
    mutationFn: () => assessmentApi.submitSelf(eaId!),
    onSuccess: () => { message.success('Self appraisal submitted'); refetch() },
    onError: (e: Error) => message.error(e.message),
  })
  const submitReportingMut = useMutation({
    mutationFn: () => assessmentApi.submitReporting(eaId!),
    onSuccess: () => { message.success('Reporting submitted — moves to reviewer'); refetch() },
    onError: (e: Error) => message.error(e.message),
  })
  const submitReviewingMut = useMutation({
    mutationFn: () => assessmentApi.submitReviewing(eaId!),
    onSuccess: () => { message.success('Reviewing submitted — final score computed'); refetch() },
    onError: (e: Error) => message.error(e.message),
  })
  const repMut = useMutation({
    mutationFn: (text: string) => assessmentApi.submitRepresentation(eaId!, text),
    onSuccess: () => { message.success('Representation submitted'); setReprOpen(false); reprForm.resetFields(); refetch() },
    onError: (e: Error) => message.error(e.message),
  })
  const hrLockMut = useMutation({
    mutationFn: () => assessmentApi.hrLock(eaId!),
    onSuccess: () => { message.success('HR Locked'); refetch() },
    onError: (e: Error) => message.error(e.message),
  })
  const hrRespondMut = useMutation({
    mutationFn: (v: { status: string; response_text: string }) =>
      assessmentApi.hrRespond(eaId!, v),
    onSuccess: () => { message.success('HR response saved'); setHrRespOpen(false); hrForm.resetFields(); refetch() },
    onError: (e: Error) => message.error(e.message),
  })
  const autoFetchMut = useMutation({
    mutationFn: () => assessmentApi.autoFetch(eaId!),
    onSuccess: (data: unknown) => {
      const d = data as { auto_fetched: number }
      message.success(`Auto-fetched ${d.auto_fetched} metrics`); refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  if (isLoading || !bundle) return <PageHeader title="Loading assessment…" />
  const ea = bundle.assessment
  const isAppraisee = ea.employee_user_id === currentUserId
  const isReporting = ea.reporting_authority_user_id === currentUserId
  const isReviewing = ea.reviewing_authority_user_id === currentUserId
  const stage = ea.current_stage
  const locked = stage === 'LOCKED'

  return (
    <>
      <PageHeader
        title={`Assessment — ${bundle.cycle?.cycle_name || ''}`}
        subtitle={`Employee: ${ea.employee_id.substring(0, 8)}…`}
        extra={
          <Space>
            <Tag color={STAGE_COLOR[stage]}>{stage}</Tag>
            {ea.normalized_score_100 != null && (
              <Tag color="green">Score: {Number(ea.normalized_score_100).toFixed(1)} {ea.performance_grade}</Tag>
            )}
            <Button onClick={() => navigate('/hr/my-assessments')}>Back</Button>
          </Space>
        }
      />

      {locked && (
        <Alert showIcon type="success" style={{ marginBottom: 12 }}
               message="Assessment LOCKED" description="No more edits. Contact HR to reopen if needed."
        />
      )}

      <Card size="small" style={{ marginBottom: 12 }}>
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="Cycle">
            {bundle.cycle ? `${bundle.cycle.cycle_code} (${bundle.cycle.period_start} → ${bundle.cycle.period_end})` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Template">
            {bundle.template?.template_name || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Stage">{stage}</Descriptions.Item>
          <Descriptions.Item label="Self submitted">
            {ea.self_submitted_at ? dayjs(ea.self_submitted_at).format('MMM D HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Reporting submitted">
            {ea.reporting_submitted_at ? dayjs(ea.reporting_submitted_at).format('MMM D HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Reviewing submitted">
            {ea.reviewing_submitted_at ? dayjs(ea.reviewing_submitted_at).format('MMM D HH:mm') : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {bundle.scores.length > 0 && (
        <Card size="small" title="Score Breakdown" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            {bundle.scores.map((s) => (
              <Col key={s.id} xs={24} md={8} lg={4}>
                <Card size="small">
                  <Text type="secondary">{DIM_LABEL[s.dimension] || s.dimension}</Text>
                  <div style={{ fontSize: 20, fontWeight: 600 }}>{Number(s.normalized_pct).toFixed(1)}%</div>
                  <Text type="secondary" style={{ fontSize: 11 }}>weight {Number(s.weight_pct).toFixed(0)}%</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      <Space style={{ marginBottom: 12 }} wrap>
        {isAppraisee && (stage === 'SELF_IN_PROGRESS' || stage === 'DRAFT') && (
          <Button type="primary" onClick={() => submitSelfMut.mutate()} loading={submitSelfMut.isPending}>
            Submit Self Appraisal
          </Button>
        )}
        {isReporting && (stage === 'REPORTING_IN_PROGRESS' || stage === 'SELF_SUBMITTED') && (
          <>
            <Button type="primary" onClick={() => submitReportingMut.mutate()} loading={submitReportingMut.isPending}>
              Submit Reporting Appraisal
            </Button>
            <Button onClick={() => autoFetchMut.mutate()} loading={autoFetchMut.isPending}>
              Auto-fetch ERP Metrics
            </Button>
          </>
        )}
        {isReviewing && (stage === 'REVIEWING_IN_PROGRESS' || stage === 'REPORTING_SUBMITTED') && (
          <Button type="primary" onClick={() => submitReviewingMut.mutate()} loading={submitReviewingMut.isPending}>
            Submit Reviewing
          </Button>
        )}
        {isAppraisee && stage === 'REVIEWING_SUBMITTED' && (
          <Button onClick={() => setReprOpen(true)}>Submit Representation</Button>
        )}
        {stage === 'REPRESENTATION' && (
          <Button onClick={() => setHrRespOpen(true)}>HR Response</Button>
        )}
        {(stage === 'REVIEWING_SUBMITTED' || stage === 'REPRESENTATION' || stage === 'HR_REVIEW') && (
          <Button danger type="primary" onClick={() => hrLockMut.mutate()} loading={hrLockMut.isPending}>
            HR Lock
          </Button>
        )}
      </Space>

      <Collapse defaultActiveKey={bundle.template?.sections?.map((s) => s.id)}>
        {bundle.template?.sections?.map((s: AssessmentSection) => (
          <Collapse.Panel
            key={s.id}
            header={
              <Space>
                <Tag>{s.applies_to_role}</Tag>
                <Text strong>{s.section_name}</Text>
                <Badge count={(s.questions || []).length} showZero offset={[8, -4]} />
              </Space>
            }
          >
            {(s.questions || []).map((q: AssessmentQuestion) => (
              <QuestionRow
                key={q.id}
                question={q}
                bundle={bundle}
                isAppraisee={isAppraisee}
                isReporting={isReporting}
                isReviewing={isReviewing}
                locked={locked}
                eaId={eaId!}
                onChange={refetch}
              />
            ))}
          </Collapse.Panel>
        ))}
      </Collapse>

      {bundle.representation.length > 0 && (
        <Card size="small" title="Representation History" style={{ marginTop: 12 }}>
          {bundle.representation.map((r) => (
            <Card key={r.id} size="small" style={{ marginBottom: 8 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Tag>{r.status}</Tag>
                  <Text type="secondary">{dayjs(r.submitted_at).format('MMM D HH:mm')}</Text>
                </Space>
                <Paragraph>{r.representation_text}</Paragraph>
                {r.hr_response_text && (
                  <Alert type="info" message="HR response"
                          description={r.hr_response_text} showIcon />
                )}
              </Space>
            </Card>
          ))}
        </Card>
      )}

      {/* Representation modal */}
      <Modal
        open={reprOpen}
        title="Submit Representation"
        onCancel={() => setReprOpen(false)}
        onOk={() => reprForm.submit()}
        confirmLoading={repMut.isPending}
      >
        <Form form={reprForm} layout="vertical"
              onFinish={(v: { text: string }) => repMut.mutate(v.text)}>
          <Form.Item name="text" label="What is your disagreement?"
                     rules={[{ required: true, min: 20 }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Alert type="info" showIcon
                 message="Your representation will be reviewed by HR before the assessment is locked." />
        </Form>
      </Modal>

      <Modal
        open={hrRespOpen}
        title="HR Response to Representation"
        onCancel={() => setHrRespOpen(false)}
        onOk={() => hrForm.submit()}
        confirmLoading={hrRespondMut.isPending}
      >
        <Form form={hrForm} layout="vertical"
              initialValues={{ status: 'PARTIAL' }}
              onFinish={(v) => hrRespondMut.mutate(v)}>
          <Form.Item name="status" label="Outcome" rules={[{ required: true }]}>
            <Input.TextArea rows={1} />
          </Form.Item>
          <Form.Item name="response_text" label="HR Response" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ─── Per-question editor ─────────────────────────────────────────────

function QuestionRow({
  question, bundle, isAppraisee, isReporting, isReviewing, locked, eaId, onChange,
}: {
  question: AssessmentQuestion
  bundle: any
  isAppraisee: boolean
  isReporting: boolean
  isReviewing: boolean
  locked: boolean
  eaId: string
  onChange: () => void
}) {
  const { message } = App.useApp()

  // Determine which role this user can answer as
  const ansRole = question.answerable_by_role
  const canEdit =
    !locked && (
      (ansRole === 'SELF' && isAppraisee) ||
      (ansRole === 'REPORTING' && isReporting) ||
      (ansRole === 'REVIEWING' && isReviewing) ||
      (ansRole === 'REPRESENTATION' && isAppraisee)
    )

  // Find existing answers (by role)
  const answers = bundle.answers.filter((a: any) => a.question_id === question.id)

  const upsertMut = useMutation({
    mutationFn: (body: {
      answer_value_numeric?: number | null
      answer_value_text?: string | null
      remarks?: string | null
    }) =>
      assessmentApi.upsertAnswer(eaId, {
        question_id: question.id,
        answered_by_role: ansRole,
        ...body,
      }),
    onSuccess: () => { message.success('Saved'); onChange() },
    onError: (e: Error) => message.error(e.message),
  })

  const myAnswer = answers.find((a: any) => a.answered_by_role === ansRole)

  return (
    <Card size="small" style={{ marginBottom: 8 }}>
      <Row gutter={12} align="middle">
        <Col xs={24} md={14}>
          <Space direction="vertical" size={2}>
            <Text strong>{question.question_text}</Text>
            <Space size={4}>
              <Tag>{question.question_type}</Tag>
              <Tag>{ansRole}</Tag>
              {question.auto_kpi_source && <Tag color="blue">auto: {question.auto_kpi_source}</Tag>}
              {question.contributes_to_dimension && (
                <Tag color="purple">dim: {question.contributes_to_dimension}</Tag>
              )}
              <Text type="secondary" style={{ fontSize: 11 }}>weight {Number(question.weight).toFixed(2)}</Text>
            </Space>
          </Space>
        </Col>
        <Col xs={24} md={10}>
          {question.question_type === 'TEXT' || question.question_type === 'DOMAIN_RECOMMENDATION' ? (
            <Input.TextArea
              rows={2}
              defaultValue={myAnswer?.answer_value_text || ''}
              disabled={!canEdit}
              placeholder={canEdit ? 'Your response…' : 'Read-only'}
              onBlur={(e) => canEdit && upsertMut.mutate({ answer_value_text: e.target.value })}
            />
          ) : (
            <Space>
              <InputNumber
                defaultValue={myAnswer?.answer_value_numeric ?? undefined}
                min={Number(question.min_value ?? 0)}
                max={Number(question.max_value ?? 5)}
                disabled={!canEdit || (ansRole as string) === 'AUTO'}
                style={{ width: 120 }}
                onBlur={(e) => {
                  if (!canEdit) return
                  const v = parseFloat((e.target as HTMLInputElement).value)
                  if (!isNaN(v)) upsertMut.mutate({ answer_value_numeric: v })
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {question.min_value ?? 0} – {question.max_value ?? 5}
              </Text>
            </Space>
          )}
          {answers.length > 1 && (
            <Space style={{ marginTop: 4 }} wrap>
              {answers.filter((a: any) => a.answered_by_role !== ansRole).map((a: any) => (
                <Tag key={a.id} color="default">
                  {a.answered_by_role}: {a.answer_value_numeric ?? a.answer_value_text ?? '—'}
                  {a.auto_fetched_flag && ' (auto)'}
                </Tag>
              ))}
            </Space>
          )}
        </Col>
      </Row>
    </Card>
  )
}
